import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { COLUMNS } from './columns'
import { LoginContext } from '../../../reactcontext/ReactContext'
import axios from 'axios'
import swal from 'sweetalert'
import ProfileTable from './ProfileTable'
import Modal from '../../../components/Modal'

const ProfileTableInit = ({setActiveTab, setSelectedProfile, refreshTrigger, initialStatusFilter = 'All'}) => {

  const apiUrl = process.env.REACT_APP_API_URL
  const [profiles, setProfiles] = useState([])
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter)
  const [errorMessage, setErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const { loginInfo } = useContext(LoginContext)
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [newStatus, setNewStatus] = useState(null)

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/employee`, {
        headers: {
          loginToken: localStorage.getItem("loginToken")
        }
      });
      if(response.data.error){
        setErrorMessage(response.data.error)
      } else {
        setProfiles(response.data)
      }
    } catch (err) {
      swal({
        title: "Error",
        icon: "error",
        text: JSON.stringify(err.message)
      })
    }
    setLoading(false);
  };

  const handleStatusToggle = (employee) => {
    const newStatusValue = employee.status === "Active" ? "Inactive" : "Active";
    setSelectedEmployee(employee);
    setNewStatus(newStatusValue);
    setIsModalOpen(true);
  };

  const confirmStatusChange = async () => {
    try {
      const response = await axios.put(`${apiUrl}/employee/${selectedEmployee.id}/status`, 
        { status: newStatus },
        {
          headers: {
            loginToken: localStorage.getItem("loginToken")
          }
        }
      );

      if (response.data.success || response.status === 200) {
        // Update the local state
        setProfiles(prevProfiles => 
          prevProfiles.map(profile => 
            profile.id === selectedEmployee.id 
              ? { ...profile, status: newStatus } 
              : profile
          )
        );
        
        swal({
          title: "Success",
          icon: "success",
          text: `Employee status changed to ${newStatus}`
        });
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      swal({
        title: "Error",
        icon: "error",
        text: "Failed to update employee status. Please try again."
      });
    }
    setIsModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEmployee(null);
    setNewStatus(null);
  };

  const filteredProfiles = useMemo(() => {
    if (statusFilter === 'All') return profiles;
    return profiles.filter(p => p.status === statusFilter);
  }, [profiles, statusFilter])

  const columns = useMemo(() => COLUMNS(handleStatusToggle), [])

  useEffect(() => {
    setSelectedProfile({})

    !loginInfo.login && navigate("/")
    
  }, [loginInfo])

  useEffect(() => {
    fetchProfiles();
  }, [refreshTrigger]);
  // console.log(profiles)
  return (
    <div className='w-full h-full overflow-y-auto overflow-x-hidden bg-gray-50'>
      <hr />
      {
        loading ? <p className='w-full h-full text-center text-lg py-5'>Fetching results ... Please wait</p> :
          profiles.length === 0 && (
              <div className='w-full text-center text-lg py-5 gap-y-10 flex flex-col justify-center items-center'>
                <p>{"You need to create a profile"}</p>

                 <button 
                  onClick={() => setActiveTab(0)}
                  className="py-4 font-light text-white px-5 border-2 rounded-xl bg-black-gradient font-poppins">
                    Add Employee
                 </button>
              </div>              
            )
      }

      {
        filteredProfiles && filteredProfiles.length > 0 &&
        <ProfileTable
          columns={columns}
          data={filteredProfiles}
          setActiveTab={setActiveTab}
          setSelectedProfile={setSelectedProfile}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          allProfiles={profiles}
        />
      }

      {/* Status Change Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        dimensions="max-w-md"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Confirm Status Change
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            Are you sure you want to change the status of{" "}
            <span className="font-semibold">
              {selectedEmployee?.firstName} {selectedEmployee?.lastName}
            </span>{" "}
            from{" "}
            <span className={`font-semibold ${selectedEmployee?.status === "Active" ? "text-green-600" : "text-gray-600"}`}>
              {selectedEmployee?.status}
            </span>{" "}
            to{" "}
            <span className={`font-semibold ${newStatus === "Active" ? "text-green-600" : "text-red-600"}`}>
              {newStatus}
            </span>
            ?
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={closeModal}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={confirmStatusChange}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ProfileTableInit
